import callApi from 'utils/api/callApi'
import { FP_ENDPOINTS } from 'apps/fleet-planner/config'
import { SUBSCRIBER_NOTIFICATION_TYPE } from 'apps/fleet-planner/constants/loads'
import { ICustomerContact } from 'apps/fleet-planner/types/customers'
import { IUpdateShipmentResponse } from 'apps/fleet-planner/types/shipment'

/**
 * Updates a shipment's trailer entity
 * @see https://fp-staging.transfix.io/api-docs/index.html#/Shipments/put_shipments__id__trailer
 */
export const updateShipmentTrailer = (
  shipmentId: string,
  trailerId: string | null
): Promise<IUpdateShipmentResponse> => {
  return callApi({
    method: 'PUT',
    endpoint: `${FP_ENDPOINTS.shipments}/${shipmentId}/trailer`,
    body: {
      shipment: {
        trailerId,
      },
    },
  })
}

/**
 * Updates a shipment's truck entity
 * @see https://fp-staging.transfix.io/api-docs/index.html#/Shipments/put_shipments__id__truck
 */
export const updateShipmentTruck = (
  shipmentId: string,
  truckId: string | null
): Promise<IUpdateShipmentResponse> => {
  return callApi({
    method: 'PUT',
    endpoint: `${FP_ENDPOINTS.shipments}/${shipmentId}/truck`,
    body: {
      shipment: {
        truckId,
      },
    },
  })
}

/**
 * Updates a shipment's dispatcher
 * @see https://fp-staging.transfix.io/api-docs/index.html#/Shipments/put_shipments__id__dispatcher
 */
export const updateShipmentDispatcher = (
  shipmentId: string,
  dispatcherId: string | null
): Promise<IUpdateShipmentResponse> => {
  return callApi({
    method: 'PUT',
    endpoint: `${FP_ENDPOINTS.shipments}/${shipmentId}/dispatcher`,
    body: {
      shipment: {
        dispatcherId,
      },
    },
  })
}

/**
 * Updates a shipment's drivers
 * @see https://fp-staging.transfix.io/api-docs/index.html#/Shipments/put_shipments__id__drivers
 */
export const updateShipmentDrivers = (
  shipmentId: string,
  driverIds: string[]
): Promise<IUpdateShipmentResponse> => {
  return callApi({
    method: 'PUT',
    endpoint: `${FP_ENDPOINTS.shipments}/${shipmentId}/drivers`,
    body: {
      shipment: {
        driverIds,
      },
    },
  })
}

/**
 * Updates a shipment's `subscribers` array
 * @method GET
 * @see https://fp-staging.transfix.io/api-docs/index.html#/Shipments/put_shipments__id__subscribers
 */
export const updateShipmentSubscribers = (
  shipmentId: string,
  newSubscribers: ICustomerContact[],
  notificationOption: SUBSCRIBER_NOTIFICATION_TYPE
): Promise<IUpdateShipmentResponse> => {
  return callApi({
    method: 'PUT',
    endpoint: `${FP_ENDPOINTS.shipments}/${shipmentId}/subscribers`,
    body: {
      shipment: {
        notify_subscribers_about: notificationOption,
        subscribers: newSubscribers,
      },
    },
  })
}

export interface IUpdatedAssignmentProps {
  dispatcherId?: string | number | null
  driverIds?: (string | number | null)[]
  motorCarrierId?: string | number | null
  tractorVin?: string | null
  trailerId?: string | number | null
  truckId?: string | number | null
}

/**
 * Update several props of a shipment at once
 * @method PUT
 */
export const updateShipmentAssignment = (
  shipmentId: string | number,
  updatedProps: IUpdatedAssignmentProps
): Promise<IUpdateShipmentResponse> => {
  return callApi({
    method: 'PUT',
    endpoint: `${FP_ENDPOINTS.shipments}/${shipmentId}/assignment`,
    body: {
      shipment: {
        ...updatedProps,
      },
    },
  })
}
